import { AppUser, getUsersInTag, IField } from '@eventr/common'
import { ColoursObj, Icon } from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useSignal, useSignalEffect } from '@preact/signals-react'
import { useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { HeaderSearchBar } from '../../../home/components/carousel/slide/footer/HeaderSearchBar'
import { useBoundStore } from '../../../state/bound.store'
import { query } from '../../../state/headerState/headerSignals'
import { IOptionsWithTypes, OptionType } from '../../../state/interfaces'
import { DepartmentLine } from '../editMembers/DepartmentLine'
import { GroupLine } from '../editMembers/GroupLine'
import { SelectAllBox } from '../editMembers/selectAllBox'
import { UserListSection } from '../editMembers/UserListSection'
import {
    checkIfUserSelected,
    checkIfUserTagSelected,
} from '../editMembers/utils'
import { useEditSectionContext } from '../editSectionProvider'

interface IEditMembersItemProps {
    field: IField
    settings?: any
    _id?: string
}
export const EditMembersItem = ({
    _id,
    field,
    settings,
}: IEditMembersItemProps) => {
    const { getValue, updateValues } = useEditSectionContext()
    const { orgConfig, selectedItineraryUsers, getEventInfoV2 } = useBoundStore(
        useShallow((s) => ({
            orgConfig: s.orgConfig,
            selectedItineraryUsers: s.selectedItineraryUsers,
            getEventInfoV2: s.getEventInfoV2,
        }))
    )

    const membersList = useSignal<IOptionsWithTypes[]>([])
    const [secondaryField, setSecondaryField] = useState<IField | null>(null)
    const itinMembers = useMemo(() => {
        return selectedItineraryUsers()
    }, [selectedItineraryUsers])

    const itinDepartments = useMemo(() => {
        const membersList = getEventInfoV2()?.data?.members ?? []
        return membersList
            .filter((u) => u.type === 'Department' || u.type === 'DepartmentV2')
            .filter((u) => {
                return orgConfig.departmentTags.some(
                    (tag) => tag.name === u.option
                )
            })
    }, [getEventInfoV2, orgConfig.departmentTags])

    const itinGroups = useMemo(() => {
        const membersList = getEventInfoV2()?.data?.members ?? []

        return membersList
            .filter((u) => u.type === 'Group' || u.type === 'GroupV2')
            .filter((u) => {
                return orgConfig.groupTags.some((tag) => tag.name === u.option)
            })
    }, [getEventInfoV2, orgConfig.groupTags])

    useSignalEffect(() => {
        let initFieldValue = []
        initFieldValue.push(...(getValue(field, _id) ?? []))
        if (settings.secondaryField) {
            const secondField = useBoundStore
                .getState()
                .getFieldById(settings.secondaryField)
            setSecondaryField(secondField)
            let secondFieldValue = getValue(secondField, _id)
            if (secondFieldValue) {
                secondFieldValue = secondFieldValue.map((u) => {
                    return { ...u, props: { secondary: true } }
                })
            }
            initFieldValue.push(...(secondFieldValue ?? []))
        }
        membersList.value = initFieldValue
    })

    const onAddUser = (userList: AppUser[]) => {
        const notSelectedUsers = userList.filter(
            (u) => !checkIfUserSelected(u.id, membersList.value)
        )

        const extendedList = notSelectedUsers.map((user) => ({
            type: 'User' as OptionType,
            option: user.id,
        }))

        membersList.value = [...membersList.value, ...extendedList]
        update()
    }
    const update = () => {
        updateValues({
            value: membersList.value.filter(
                (u) => u.props?.secondary === undefined
            ),
            field: field,
            _id: _id,
        })
    }

    // Used when 2 list of users managed by this component
    const updateSecondary = () => {
        updateValues({
            value: membersList.value.filter((u) => u.props?.secondary === true),
            field: secondaryField,
            _id: _id,
        })
    }

    const onRemoveUser = (userList: AppUser[]) => {
        const idsToFilter = userList.map((u) => {
            return u.id
        })
        const filteredList = membersList.value.filter((member) => {
            return !idsToFilter.includes(member.option as number)
        })
        membersList.value = filteredList
        update()
    }
    const onAddDepartment = (department: string) => {
        membersList.value = [
            ...membersList.value,
            {
                type: 'DepartmentV2',
                option: department,
            },
        ]
        const usersToAdd = getUsersInTag(
            { name: department, type: 'Department' },
            itinMembers
        )
        onAddUser(usersToAdd)
    }

    const onRemoveDepartment = (department: string) => {
        const tmpMembersListTemp = membersList.value.filter((member) => {
            if (
                (member.type === 'Department' ||
                    member.type === 'DepartmentV2') &&
                member.option === department
            ) {
                return false
            } else {
                return true
            }
        })
        membersList.value = [...tmpMembersListTemp]

        const tagUsers = getUsersInTag(
            { name: department, type: 'Department' },
            itinMembers
        )
        onRemoveUser(tagUsers)
    }

    const onAddGroup = (group: string) => {
        membersList.value = [
            ...membersList.value,
            {
                type: 'GroupV2',
                option: group,
            },
        ]
        const usersToAdd = getUsersInTag(
            { name: group, type: 'Group' },
            itinMembers
        )
        onAddUser(usersToAdd)
    }
    const onRemoveGroup = (group: string) => {
        const tmpMembersListTemp = membersList.value.filter((member) => {
            if (
                (member.type === 'Group' || member.type === 'GroupV2') &&
                member.option === group
            ) {
                return false
            } else {
                return true
            }
        })
        membersList.value = [...tmpMembersListTemp]

        const tagUsers = getUsersInTag(
            { name: group, type: 'Group' },
            itinMembers
        )
        onRemoveUser(tagUsers)
    }

    const secondaryFieldControlBox = (user: AppUser) => {
        if (settings.secondaryField) {
            const isInList = membersList.value.some((u) => {
                return u.option === user.id && u.type === 'User'
            })
            if (!isInList) return null
            const isInSecondaryField = membersList.value.some((u) => {
                return (
                    u.option === user.id &&
                    u.type === 'User' &&
                    u.props?.secondary
                )
            })
            return (
                <Box>
                    <Icon
                        iconColour={
                            isInSecondaryField
                                ? ColoursObj.core.secondary
                                : ColoursObj.shades.shade3
                        }
                        name={settings.secondaryFieldIcon}
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            if (isInSecondaryField) {
                                const filteredList = membersList
                                    .peek()
                                    .filter((member) => {
                                        return (
                                            member.option !== user.id ||
                                            member.type !== 'User' ||
                                            !member.props?.secondary
                                        )
                                    })
                                membersList.value = [
                                    ...filteredList,
                                    {
                                        type: 'User' as OptionType,
                                        option: user.id,
                                    },
                                ]
                            } else {
                                const filteredList = membersList
                                    .peek()
                                    .filter((member) => {
                                        return (
                                            member.option !== user.id ||
                                            member.type !== 'User'
                                        )
                                    })
                                membersList.value = [
                                    ...filteredList,
                                    {
                                        type: 'User' as OptionType,
                                        option: user.id,
                                        props: { secondary: true },
                                    },
                                ]
                            }
                            updateSecondary()
                            update()
                        }}
                    />
                </Box>
            )
        } else {
            return null
        }
    }

    const departmentsListSection = () => {
        if (itinDepartments.length > 0) {
            return itinDepartments.map((depTag, i) => {
                return (
                    <DepartmentLine
                        key={i}
                        allUsers={itinMembers}
                        membersList={membersList.value}
                        tag={{ name: depTag.option, color: '' }}
                        onAddDepartment={onAddDepartment}
                        onRemoveDepartment={onRemoveDepartment}
                        onRemoveUser={(u) => onRemoveUser([u])}
                        onAddUser={(u) => onAddUser([u])}
                        allowSelfRemove={true}
                        userControlBox={secondaryFieldControlBox}
                    />
                )
            })
        } else {
            return null
        }
    }
    const groupsListSection = () => {
        if (itinGroups.length > 0) {
            return itinGroups.map((groupTag, i) => {
                return (
                    <GroupLine
                        key={i}
                        allUsers={itinMembers}
                        tag={{ name: groupTag.option, color: '' }}
                        membersList={membersList.value}
                        onAddGroup={onAddGroup}
                        onRemoveGroup={onRemoveGroup}
                        onRemoveUser={(u) => onRemoveUser([u])}
                        onAddUser={(u) => onAddUser([u])}
                        allowSelfRemove={true}
                        userControlBox={secondaryFieldControlBox}
                    />
                )
            })
        } else {
            return null
        }
    }
    const selectAllBox = () => {
        const allSelected = itinMembers.every(
            (user) =>
                checkIfUserSelected(user.id, membersList.value) ||
                checkIfUserTagSelected(user.id, membersList.value, itinMembers)
        )
        return (
            <SelectAllBox
                allSelected={allSelected}
                allUsers={itinMembers}
                query={query.value}
                onClick={() => {
                    if (allSelected) {
                        membersList.value = []
                    } else {
                        const allUsers = itinMembers.map((user) => {
                            return {
                                type: 'User' as OptionType,
                                option: user.id,
                            }
                        })
                        membersList.value = allUsers
                    }
                    update()
                }}
            />
        )
    }
    const usersListSection = () => (
        <UserListSection
            allUsers={itinMembers}
            onAddUser={onAddUser}
            onRemoveUser={onRemoveUser}
            membersList={membersList.value}
            allowSelfRemove={true}
            userControlBox={secondaryFieldControlBox}
            ignoreTaggedUsers
            tagsToIgnore={[
                ...itinGroups.map((g) => {
                    return { option: g.option, type: 'Group' as OptionType }
                }),
                ...itinDepartments.map((d) => {
                    return {
                        option: d.option,
                        type: 'Department' as OptionType,
                    }
                }),
            ]}
        />
    )
    return (
        <Stack>
            <HeaderSearchBar />
            <Box height="8px" />
            {selectAllBox()}
            <Box height="8px" />
            {departmentsListSection()}
            {groupsListSection()}
            {usersListSection()}
        </Stack>
    )
}
